// WebMCP: o painel oferece as ferramentas da loja ao agente de IA do próprio
// navegador (`document.modelContext.registerTool`, o rascunho do W3C; o Chrome
// antigo expunha em `navigator.modelContext`). O catálogo é o MESMO do servidor
// MCP — vem de `GET /mcp/ferramentas` — e a execução vai ao servidor com o
// login de quem está no painel, que refaz a cadeia de acesso como em qualquer
// rota.
//
// O que gasta crédito, publica ou mexe em dinheiro (`consequente`) tem uma
// segunda trava aqui: quando o agente manda `confirmar: true`, a pessoa vê o
// pedido NA TELA e aprova ou não. O agente não consegue se auto-confirmar.
import { useEffect } from 'react';
import { create } from 'zustand';
import { administra, useLoja } from '../../shared/estado/loja';
import { Confirmacao } from '../../shared/ui/Modal';
import { executarFerramenta, listarFerramentas, type FerramentaDoPainel } from './api';

interface FerramentaDoNavegador {
  name: string;
  title?: string;
  description: string;
  inputSchema?: object;
  annotations?: { readOnlyHint?: boolean; consequentialHint?: boolean; untrustedContentHint?: boolean };
  execute: (entrada: object, opcoes?: { signal?: AbortSignal }) => Promise<unknown>;
}
interface ContextoDoModelo {
  registerTool(f: FerramentaDoNavegador, opcoes?: { signal?: AbortSignal }): unknown;
}

/** O `modelContext` do navegador, se ele tiver um agente com WebMCP. */
export function contextoDoModelo(): ContextoDoModelo | null {
  const doc = (globalThis.document as unknown as { modelContext?: ContextoDoModelo } | undefined)?.modelContext;
  const nav = (globalThis.navigator as unknown as { modelContext?: ContextoDoModelo } | undefined)?.modelContext;
  const mc = doc ?? nav;
  return mc && typeof mc.registerTool === 'function' ? mc : null;
}
export const webMcpDisponivel = () => contextoDoModelo() !== null;

interface PedidoDoAgente { titulo: string; argumentos: Record<string, unknown>; responder: (sim: boolean) => void }
const usePedidoDoAgente = create<{ pedido: PedidoDoAgente | null; definir: (p: PedidoDoAgente | null) => void }>((set) => ({
  pedido: null,
  definir: (pedido) => set({ pedido }),
}));

/** Mostra o pedido na tela e espera a pessoa. Um de cada vez: um segundo pedido no meio recusa o primeiro. */
function pedirOkNaTela(titulo: string, argumentos: Record<string, unknown>): Promise<boolean> {
  return new Promise((resolver) => {
    usePedidoDoAgente.getState().pedido?.responder(false);
    usePedidoDoAgente.getState().definir({ titulo, argumentos, responder: (sim) => { usePedidoDoAgente.getState().definir(null); resolver(sim); } });
  });
}

const recusado = { content: [{ type: 'text', text: 'A pessoa não autorizou esta ação na tela do BoraInbox.' }], isError: true };

export async function executarDoAgente(loja: string, f: Pick<FerramentaDoPainel, 'name' | 'title' | 'consequente'>, entrada: unknown) {
  const argumentos = entrada && typeof entrada === 'object' && !Array.isArray(entrada) ? (entrada as Record<string, unknown>) : {};
  if (f.consequente && argumentos.confirmar === true && !(await pedirOkNaTela(f.title, argumentos))) return recusado;
  return executarFerramenta(loja, f.name, argumentos);
}

/** Registra o catálogo enquanto a loja estiver aberta; troca de loja (ou sair) desfaz o registro. */
export function FerramentasDoNavegador() {
  const loja = useLoja((s) => s.tenantId);
  const papel = useLoja((s) => s.papel);
  const pedido = usePedidoDoAgente((s) => s.pedido);

  useEffect(() => {
    const mc = contextoDoModelo();
    // Só dono e admin: o suporte não age por IA em loja alheia (o servidor do MCP recusa).
    if (!mc || !loja || !papel || !administra(papel) || papel === 'suporte') return;
    const controle = new AbortController();
    const desfazer: (() => void)[] = [];
    listarFerramentas(loja).then((lista) => {
      if (controle.signal.aborted) return;
      for (const f of lista) {
        try {
          const r = mc.registerTool({
            name: f.name, title: f.title, description: f.description, inputSchema: f.inputSchema,
            annotations: { readOnlyHint: f.annotations.readOnlyHint, consequentialHint: f.consequente, untrustedContentHint: true },
            execute: (entrada) => executarDoAgente(loja, f, entrada),
          }, { signal: controle.signal }) as { unregister?: () => void } | Promise<unknown> | undefined;
          if (r && typeof (r as Promise<unknown>).catch === 'function') (r as Promise<unknown>).catch(() => undefined);
          else if (r && typeof (r as { unregister?: () => void }).unregister === 'function') desfazer.push((r as { unregister: () => void }).unregister);
        } catch { /* nome repetido de um registro anterior: segue com as outras */ }
      }
    }).catch(() => undefined);
    return () => {
      controle.abort();
      for (const d of desfazer) { try { d(); } catch { /* já saiu */ } }
    };
  }, [loja, papel]);

  const resumo = pedido ? JSON.stringify(Object.fromEntries(Object.entries(pedido.argumentos).filter(([k]) => k !== 'confirmar')), null, 2) : '';
  return (
    <Confirmacao
      pedido={pedido ? {
        titulo: 'O assistente do navegador pede o seu OK',
        mensagem: (
          <>
            <p><strong>{pedido.titulo}</strong></p>
            <p className="apoio">Isto pode gastar créditos de IA, publicar ou mexer no orçamento de anúncios. Confira:</p>
            <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto', fontSize: 12 }}>{resumo.length > 1500 ? resumo.slice(0, 1500) + '…' : resumo}</pre>
          </>
        ),
        confirmar: 'Autorizar',
      } : null}
      aoResponder={(sim) => pedido?.responder(sim)}
    />
  );
}
